// src/services/serviceMonitor.js
import api from './api';

/**
 * Service Monitor
 * 
 * This utility monitors the health of the backend service and provides
 * methods to handle server crashes and display appropriate UI notifications.
 */
class ServiceMonitor {
  constructor() {
    this.isServerDown = false;
    this.listeners = [];
    this.errorListeners = [];
    this.checkInterval = null;
    this.lastCheck = 0;
    this.consecutiveFailures = 0;
    this.MAX_FAILURES = 3;
    this.CHECK_INTERVAL = 60000; // 1 minute
  }
  
  /**
   * Start monitoring the service health
   */
  start() {
    if (this.checkInterval) {
      return; // Already running
    }
    
    console.log('Service Monitor: Starting monitoring');
    this.checkServerHealth();
    
    this.checkInterval = setInterval(() => {
      this.checkServerHealth();
    }, this.CHECK_INTERVAL);
  }
  
  /**
   * Stop monitoring
   */
  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('Service Monitor: Monitoring stopped');
    }
  }
  
  /**
   * Check if the server is healthy
   */
  async checkServerHealth() {
    // Don't check too frequently
    const now = Date.now();
    if (now - this.lastCheck < 15000) {
      return;
    }
    
    this.lastCheck = now;
    
    try {
      console.log('Service Monitor: Checking server health...');
      const result = await api.testConnection();
      
      if (result && result.status === 'offline') {
        this.consecutiveFailures++;
        console.log(`Service Monitor: Server reported offline (${this.consecutiveFailures}/${this.MAX_FAILURES})`);
      } else {
        // Reset failure count on success
        if (this.isServerDown) {
          console.log('Service Monitor: Server recovered from down state');
          this.isServerDown = false;
          this.notifyListeners(false);
        }
        
        this.consecutiveFailures = 0;
      }
    } catch (error) {
      this.consecutiveFailures++;
      console.error(`Service Monitor: Server health check failed (${this.consecutiveFailures}/${this.MAX_FAILURES}):`, error);
      
      if (error.isServerDown) {
        this.handleServerDown();
      } else {
        this.notifyErrorListeners(error);
      }
    }
    
    // If we've had multiple failures, mark the server as down
    if (this.consecutiveFailures >= this.MAX_FAILURES && !this.isServerDown) {
      this.handleServerDown();
    }
  }
  
  /**
   * Handle the server down scenario
   */
  handleServerDown() {
    if (!this.isServerDown) {
      console.log('Service Monitor: Server appears to be down');
      this.isServerDown = true;
      this.notifyListeners(true);
    }
  }
  
  /**
   * Register a listener for server status changes
   * @param {Function} listener - Function to call when status changes
   * @returns {Function} Unregister function
   */
  onServerStatusChange(listener) {
    this.listeners.push(listener);
    
    // Call with current status
    if (this.isServerDown) {
      listener(true);
    }
    
    // Return unregister function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }
  
  /**
   * Register a listener for service errors
   * @param {Function} listener - Function to call when errors occur
   * @returns {Function} Unregister function
   */
  onServiceError(listener) {
    this.errorListeners.push(listener);
    
    // Return unregister function
    return () => {
      this.errorListeners = this.errorListeners.filter(l => l !== listener);
    };
  }
  
  /**
   * Notify all registered listeners of a status change
   * @param {boolean} isDown - Whether the server is down
   */
  notifyListeners(isDown) {
    this.listeners.forEach(listener => {
      try {
        listener(isDown);
      } catch (error) {
        console.error('Service Monitor: Error in status listener', error);
      }
    });
  }
  
  /**
   * Notify all registered error listeners
   * @param {Error} error - The error that occurred
   */
  notifyErrorListeners(error) {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (listenerError) {
        console.error('Service Monitor: Error in error listener', listenerError);
      }
    });
  }
  
  /**
   * Get the current server status
   * @returns {boolean} Whether the server is considered down
   */
  isDown() {
    return this.isServerDown;
  }
  
  /**
   * Manually trigger a server health check
   * @returns {Promise<void>}
   */
  async forceCheck() {
    await this.checkServerHealth();
  }
}

// Create singleton instance
const instance = new ServiceMonitor();

// Export the singleton
export default instance;